import { create } from 'zustand';
import { persist } from 'zustand/middleware';

let _devtoolsEnabled = false;

export interface WetagOverride {
  tagId: string;
  namespaceId: string;
  entityId: string;
  value: boolean;
  createdAt: number;
}

export interface WetagLogEntry {
  id: string;
  type: 'call' | 'rerender' | 'cache-hit' | 'prefetch' | 'invalidate';
  tagId: string;
  namespaceId: string;
  entityId: string;
  result: boolean | undefined;
  timestamp: number;
  queryString?: string;
  duration?: number;
  stack?: string;
}

export interface ObservedWetag {
  tagId: string;
  namespaceId: string;
  entityId: string;
  currentValue: boolean | undefined;
  callCount: number;
  lastCalled: number;
  isOverridden: boolean;
  overrideValue?: boolean;
}

export type DevtoolsView = 'overrides' | 'developer';
export type DeveloperTab = 'logs' | 'playground';

export type QueryBlockType = 'entity_has' | 'and' | 'not';

export interface EntityHasBlock {
  id: string;
  type: 'entity_has';
  entityId: string;
  tagId: string;
}

export interface AndBlock {
  id: string;
  type: 'and';
}

export interface NotBlock {
  id: string;
  type: 'not';
}

export type QueryBlock = EntityHasBlock | AndBlock | NotBlock;

export interface PlaygroundState {
  blocks: QueryBlock[];
  previewQuery: string;
  lastResult: boolean | null;
  isExecuting: boolean;
  error: string | null;
}

interface WetagDevtoolsState {
  isOpen: boolean;
  currentView: DevtoolsView;
  developerTab: DeveloperTab;
  overrides: Map<string, WetagOverride>;
  logs: WetagLogEntry[];
  observedWetags: Map<string, ObservedWetag>;
  playground: PlaygroundState;
  logFilter: string;
  maxLogs: number;
}

interface WetagDevtoolsActions {
  toggle: () => void;
  open: () => void;
  close: () => void;
  setView: (view: DevtoolsView) => void;
  setDeveloperTab: (tab: DeveloperTab) => void;
  setLogFilter: (filter: string) => void;
  clearLogs: () => void;
  addPlaygroundBlock: (block: QueryBlock) => void;
  removePlaygroundBlock: (id: string) => void;
  updatePlaygroundBlock: (id: string, updates: Partial<QueryBlock>) => void;
  clearPlayground: () => void;
  setPlaygroundResult: (result: boolean | null, error?: string | null) => void;
  setPlaygroundExecuting: (isExecuting: boolean) => void;
  reorderPlaygroundBlocks: (fromIndex: number, toIndex: number) => void;
}

type WetagDevtoolsStore = WetagDevtoolsState & WetagDevtoolsActions;

const getOverrideKey = (namespaceId: string, tagId: string, entityId: string) =>
  `${namespaceId}::${tagId}::${entityId}`;

const getObservedKey = (namespaceId: string, tagId: string, entityId: string) =>
  `${namespaceId}::${tagId}::${entityId}`;

const blocksToQueryString = (blocks: QueryBlock[]): string => {
  if (blocks.length === 0) return '';
  const parts: string[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'entity_has') {
      parts.push(`entity_has("${block.entityId}", "${block.tagId}")`);
    } else if (block.type === 'not') {
      const nextBlock = blocks[i + 1];
      if (nextBlock && nextBlock.type === 'entity_has') {
        parts.push(`NOT entity_has("${nextBlock.entityId}", "${nextBlock.tagId}")`);
        i++;
      }
    }
    i++;
  }
  return parts.join(' AND ');
};

const initialPlaygroundState: PlaygroundState = {
  blocks: [],
  previewQuery: '',
  lastResult: null,
  isExecuting: false,
  error: null,
};

export const useWetagDevtoolsStore = create<WetagDevtoolsStore>()(
  persist(
    (set) => ({
      isOpen: false,
      currentView: 'overrides',
      developerTab: 'logs',
      overrides: new Map(),
      logs: [],
      observedWetags: new Map(),
      playground: initialPlaygroundState,
      logFilter: '',
      maxLogs: 500,

      toggle: () => set((s) => ({ isOpen: !s.isOpen })),
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      setView: (view) => set({ currentView: view }),
      setDeveloperTab: (tab) => set({ developerTab: tab }),
      setLogFilter: (filter) => set({ logFilter: filter }),
      clearLogs: () => set({ logs: [] }),

      addPlaygroundBlock: (block) =>
        set((s) => {
          const newBlocks = [...s.playground.blocks, block];
          return {
            playground: {
              ...s.playground,
              blocks: newBlocks,
              previewQuery: blocksToQueryString(newBlocks),
              error: null,
            },
          };
        }),

      removePlaygroundBlock: (id) =>
        set((s) => {
          const newBlocks = s.playground.blocks.filter((b) => b.id !== id);
          return {
            playground: {
              ...s.playground,
              blocks: newBlocks,
              previewQuery: blocksToQueryString(newBlocks),
            },
          };
        }),

      updatePlaygroundBlock: (id, updates) =>
        set((s) => {
          const newBlocks = s.playground.blocks.map((b) => (b.id === id ? { ...b, ...updates } : b));
          return {
            playground: {
              ...s.playground,
              blocks: newBlocks as QueryBlock[],
              previewQuery: blocksToQueryString(newBlocks as QueryBlock[]),
            },
          };
        }),

      clearPlayground: () => set({ playground: initialPlaygroundState }),

      setPlaygroundResult: (result, error = null) =>
        set((s) => ({
          playground: { ...s.playground, lastResult: result, error, isExecuting: false },
        })),

      setPlaygroundExecuting: (isExecuting) =>
        set((s) => ({
          playground: { ...s.playground, isExecuting, error: null },
        })),

      reorderPlaygroundBlocks: (fromIndex, toIndex) =>
        set((s) => {
          const newBlocks = [...s.playground.blocks];
          const [removed] = newBlocks.splice(fromIndex, 1);
          newBlocks.splice(toIndex, 0, removed);
          return {
            playground: {
              ...s.playground,
              blocks: newBlocks,
              previewQuery: blocksToQueryString(newBlocks),
            },
          };
        }),
    }),
    {
      name: 'wetag-devtools-storage',
      partialize: (state) => ({
        overrides: Array.from(state.overrides.entries()),
        maxLogs: state.maxLogs,
      }),
      merge: (persisted, current) => {
        const data = persisted as { overrides?: [string, WetagOverride][]; maxLogs?: number };
        return {
          ...current,
          overrides: new Map(data?.overrides || []),
          maxLogs: data?.maxLogs || current.maxLogs,
        };
      },
    }
  )
);

export const wetagDevtools = {
  enable() {
    _devtoolsEnabled = true;
  },

  isEnabled() {
    return _devtoolsEnabled;
  },

  log(
    type: WetagLogEntry['type'],
    namespaceId: string,
    tagId: string,
    entityId: string,
    result?: boolean,
    extra?: { queryString?: string; duration?: number; stack?: string }
  ) {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const entry: WetagLogEntry = {
      id: crypto.randomUUID(),
      type,
      tagId,
      namespaceId,
      entityId,
      result,
      timestamp: Date.now(),
      queryString: extra?.queryString,
      duration: extra?.duration,
      stack: extra?.stack,
    };
    useWetagDevtoolsStore.setState({
      logs: [entry, ...state.logs].slice(0, state.maxLogs),
    });
  },

  observe(namespaceId: string, tagId: string, entityId: string, result: boolean | undefined) {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const key = getObservedKey(namespaceId, tagId, entityId);
    const overrideKey = getOverrideKey(namespaceId, tagId, entityId);
    const existing = state.observedWetags.get(key);
    const override = state.overrides.get(overrideKey);

    const newObserved = new Map(state.observedWetags);
    newObserved.set(key, {
      tagId,
      namespaceId,
      entityId,
      currentValue: result,
      callCount: existing ? existing.callCount + 1 : 1,
      lastCalled: Date.now(),
      isOverridden: !!override,
      overrideValue: override?.value,
    });
    useWetagDevtoolsStore.setState({ observedWetags: newObserved });
  },

  getOverride(namespaceId: string, tagId: string, entityId: string): boolean | undefined {
    if (!_devtoolsEnabled) return undefined;
    const state = useWetagDevtoolsStore.getState();
    const key = getOverrideKey(namespaceId, tagId, entityId);
    return state.overrides.get(key)?.value;
  },

  hasOverride(namespaceId: string, tagId: string, entityId: string): boolean {
    if (!_devtoolsEnabled) return false;
    const state = useWetagDevtoolsStore.getState();
    const key = getOverrideKey(namespaceId, tagId, entityId);
    return state.overrides.has(key);
  },

  addOverride(namespaceId: string, tagId: string, entityId: string, value: boolean) {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const key = getOverrideKey(namespaceId, tagId, entityId);
    const newOverrides = new Map(state.overrides);
    newOverrides.set(key, { namespaceId, tagId, entityId, value, createdAt: Date.now() });

    const obsKey = getObservedKey(namespaceId, tagId, entityId);
    const newObserved = new Map(state.observedWetags);
    const existing = newObserved.get(obsKey);
    if (existing) {
      newObserved.set(obsKey, { ...existing, isOverridden: true, overrideValue: value });
    }

    useWetagDevtoolsStore.setState({ overrides: newOverrides, observedWetags: newObserved });
  },

  removeOverride(namespaceId: string, tagId: string, entityId: string) {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const key = getOverrideKey(namespaceId, tagId, entityId);
    const newOverrides = new Map(state.overrides);
    newOverrides.delete(key);

    const obsKey = getObservedKey(namespaceId, tagId, entityId);
    const newObserved = new Map(state.observedWetags);
    const existing = newObserved.get(obsKey);
    if (existing) {
      newObserved.set(obsKey, { ...existing, isOverridden: false, overrideValue: undefined });
    }

    useWetagDevtoolsStore.setState({ overrides: newOverrides, observedWetags: newObserved });
  },

  toggleOverride(namespaceId: string, tagId: string, entityId: string) {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const key = getOverrideKey(namespaceId, tagId, entityId);
    const existing = state.overrides.get(key);
    if (existing) {
      const newOverrides = new Map(state.overrides);
      newOverrides.set(key, { ...existing, value: !existing.value });
      useWetagDevtoolsStore.setState({ overrides: newOverrides });
    }
  },

  clearOverrides() {
    if (!_devtoolsEnabled) return;
    const state = useWetagDevtoolsStore.getState();
    const newObserved = new Map(state.observedWetags);
    newObserved.forEach((value, key) => {
      newObserved.set(key, { ...value, isOverridden: false, overrideValue: undefined });
    });
    useWetagDevtoolsStore.setState({ overrides: new Map(), observedWetags: newObserved });
  },
};

export type { WetagDevtoolsState, WetagDevtoolsActions, WetagDevtoolsStore };
